// Fine-grained reactivity for browser islands.
// Signal changes update only the DOM nodes that read them — no VDOM diffing.

type Subscriber = () => void
// biome-ignore lint/suspicious/noConfusingVoidType: void in this union is intentional — it lets createEffect accept functions that return nothing without an explicit `undefined` return
type Cleanup = (() => void) | void

let currentSubscriber: Subscriber | null = null
let currentCleanups: (() => void)[] | null = null
let batchDepth = 0
const pendingEffects = new Set<Subscriber>()

export type Signal<T> = [get: () => T, set: (value: T) => void]
export type ReadonlySignal<T> = () => T

/**
 * Create a reactive signal — a getter/setter pair.
 * Reading the getter inside an effect or reactive JSX child subscribes that
 * context to future updates; calling the setter notifies all subscribers.
 *
 * @example
 * const [count, setCount] = createSignal(0)
 * count()            // read: 0
 * setCount(1)        // write
 * setCount(n => n + 1)  // updater function
 */
export function createSignal<T>(initialValue: T): Signal<T> {
  let value = initialValue
  const subscribers = new Set<Subscriber>()

  const get = (): T => {
    if (currentSubscriber) subscribers.add(currentSubscriber)
    return value
  }

  const set = (next: T): void => {
    if (Object.is(value, next)) return
    value = next
    if (batchDepth > 0) {
      for (const sub of subscribers) pendingEffects.add(sub)
    } else {
      for (const sub of [...subscribers]) sub()
    }
  }

  return [get, set]
}

/**
 * Run a side effect immediately and re-run it whenever its reactive
 * dependencies change. The effect automatically tracks which signals are
 * read during execution and subscribes to each of them.
 *
 * Return a cleanup function (or nothing) — it runs before the next execution
 * and when the enclosing scope is disposed.
 *
 * @example
 * createEffect(() => {
 *   document.title = name()  // re-runs whenever `name` changes
 * })
 */
export function createEffect(fn: () => Cleanup): void {
  // Use an object wrapper so the parent-scope disposal closure always reads
  // the current cleanups array, not a stale reference from an earlier run.
  const state = { cleanups: [] as (() => void)[] }

  // If created inside a parent scope (createRoot or another createEffect),
  // register disposal so this effect's cleanups run when the parent disposes.
  if (currentCleanups) {
    currentCleanups.push(() => {
      for (const c of state.cleanups) c()
      state.cleanups = []
    })
  }

  const run: Subscriber = () => {
    for (const c of state.cleanups) c()
    state.cleanups = []

    const prev = currentSubscriber
    const prevCleanups = currentCleanups
    currentSubscriber = run
    currentCleanups = state.cleanups
    try {
      const cleanup = fn()
      if (cleanup) state.cleanups.push(cleanup)
    } finally {
      currentSubscriber = prev
      currentCleanups = prevCleanups
    }
  }

  run()
}

/**
 * Create a memoized derived value. Recomputes only when its reactive
 * dependencies change; all readers share the same cached result.
 *
 * @example
 * const fullName = createMemo(() => `${firstName()} ${lastName()}`)
 * <h1>{() => fullName()}</h1>
 */
export function createMemo<T>(fn: () => T): ReadonlySignal<T> {
  const [get, set] = createSignal<T>(undefined as T)
  createEffect(() => {
    set(fn())
  })
  return get
}

/**
 * Register a teardown tied to the current reactive scope.
 * Called before the enclosing effect re-runs, or when the root is disposed.
 * No-op if called outside any reactive scope.
 *
 * @example
 * createEffect(() => {
 *   const id = setInterval(tick, 1000)
 *   onCleanup(() => clearInterval(id))
 * })
 */
export function onCleanup(fn: () => void): void {
  currentCleanups?.push(fn)
}

/**
 * Create a reactive scope with an explicit lifetime. The `dispose` function
 * tears down all effects and cleanups registered within the scope.
 * Useful for managing reactive state outside the normal component lifecycle.
 *
 * @example
 * const dispose = createRoot((d) => {
 *   createEffect(() => console.log(count()))
 *   return d
 * })
 * dispose()  // tear everything down
 */
export function createRoot<T>(fn: (dispose: () => void) => T): T {
  const cleanups: (() => void)[] = []
  const dispose = (): void => {
    for (const c of cleanups) c()
    cleanups.length = 0
  }
  const prev = currentCleanups
  currentCleanups = cleanups
  try {
    return fn(dispose)
  } finally {
    currentCleanups = prev
  }
}

/**
 * Read signals inside `fn` without registering reactive dependencies.
 * The enclosing effect will not re-run when those signals change.
 *
 * @example
 * createEffect(() => {
 *   const a = a()              // tracked — effect re-runs when `a` changes
 *   const b = untrack(() => b())  // not tracked
 * })
 */
export function untrack<T>(fn: () => T): T {
  const prev = currentSubscriber
  currentSubscriber = null
  try {
    return fn()
  } finally {
    currentSubscriber = prev
  }
}

/**
 * Batch multiple signal writes into a single downstream notification pass.
 * Without batching, each setter triggers its own update cycle.
 *
 * @example
 * batch(() => {
 *   setX(1)
 *   setY(2)  // effects that read both x and y run once, not twice
 * })
 */
export function batch<T>(fn: () => T): T {
  batchDepth++
  try {
    return fn()
  } finally {
    batchDepth--
    if (batchDepth === 0) {
      const toRun = [...pendingEffects]
      pendingEffects.clear()
      for (const sub of toRun) sub()
    }
  }
}

// ─── Store integration hooks ──────────────────────────────────────────────────
// Used by store.ts to participate in the same subscriber graph and batching.

export function trackRead(subs: Set<Subscriber>): void {
  if (currentSubscriber) subs.add(currentSubscriber)
}

export function notifySubs(subs: Set<Subscriber>): void {
  if (batchDepth > 0) {
    for (const sub of subs) pendingEffects.add(sub)
  } else {
    for (const sub of [...subs]) sub()
  }
}
